import { Injectable, signal, inject } from '@angular/core';
import { StringOutputParser } from '@langchain/core/output_parsers';
import { ChatPromptTemplate } from '@langchain/core/prompts';
import { ChatOpenAI } from '@langchain/openai';
import { assistant, speechToText, textToSpeech } from '@runnables';
import { OpenAI } from 'openai';
import { AiXpertService } from '@services';

const OPEN_API_KEY =
  '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';

const CHUNK_DURATION = 5000;
const SILENCE_DURATION = 1000;
const SILENCE_THRESHOLD = 0.01;

@Injectable({
  providedIn: 'root',
})
export class AiVoiceService {
  #aiXpert = inject(AiXpertService);

  question = signal('');
  answer = signal('');
  recrding = signal(false);
  idle = signal(false);

  chatOpenAi = new ChatOpenAI({
    apiKey: OPEN_API_KEY,
  });

  openAi = new OpenAI({
    apiKey: OPEN_API_KEY,
    dangerouslyAllowBrowser: true,
  });

  thread = this.openAi.beta.threads.create();

  mediaRecorder!: MediaRecorder;
  stream!: MediaStream;
  isRecording = signal(false);

  audioContext!: AudioContext;
  analyser!: AnalyserNode;
  silenceTimer: any = null;
  volumeCheckInterval: any;

  tempUrl: string | null = null;

  record() {
    this.startRecording();
  }

  async startRecording() {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const audioInputDevices = devices.filter(
      (device) => device.kind === 'audioinput',
    );

    if (audioInputDevices.length === 0) {
      throw new Error('No audio input devices found.');
    }

    const selectedDeviceId = audioInputDevices[0].deviceId;

    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: { deviceId: selectedDeviceId },
    });

    let mimeType;

    if (MediaRecorder.isTypeSupported('audio/webm')) {
      mimeType = 'audio/webm';
    } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
      mimeType = 'audio/mp4';
    }

    if (mimeType) {
      this.mediaRecorder = new MediaRecorder(this.stream, {
        mimeType,
      });
      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.tempUrl = URL.createObjectURL(event.data);
          if (!this.isRecording()) {
            this.sendVoice();
          }
        }
      };

      this.mediaRecorder.start(CHUNK_DURATION);
      this.isRecording.set(true);
      console.log('Recording started...');

      this.audioContext = new AudioContext();
      const source = this.audioContext.createMediaStreamSource(this.stream);
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = 2048;

      source.connect(this.analyser);
      this.detectSilence();
    }
  }

  stopRecording() {
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop();
      this.stream.getTracks().forEach((track) => track.stop());
      this.audioContext.close();
      this.isRecording.set(false);
    }
  }

  detectSilence() {
    const floatBuffer = new Float32Array(this.analyser.fftSize);
    this.volumeCheckInterval = setInterval(() => {
      this.analyser.getFloatTimeDomainData(floatBuffer);
      const rms = Math.sqrt(
        floatBuffer.reduce((acc, val) => acc + val * val, 0) /
          floatBuffer.length,
      );

      if (rms < SILENCE_THRESHOLD) {
        if (!this.silenceTimer) {
          this.silenceTimer = setTimeout(() => {
            this.stopRecording();
            clearInterval(this.volumeCheckInterval);
            clearTimeout(this.silenceTimer);
            this.silenceTimer = null;
            console.log('Recording stopped due to silence.');
          }, SILENCE_DURATION);
        }
      } else {
        if (this.silenceTimer) {
          clearTimeout(this.silenceTimer);
          this.silenceTimer = null;
        }
      }
    }, 100);
  }

  async sendVoice() {
    this.idle.set(true);
    const thread = await this.thread;
    const composedChain = speechToText(this.tempUrl)
      .pipe((transcription) => {
        console.log('Transcription:', transcription);
        this.question.set(transcription);
        return transcription;
      })
      .pipe((transcription) => {
        const prompt = ChatPromptTemplate.fromTemplate(
          `Answer the following question as clearly as possible:\n\n${transcription}`,
        );
        return prompt;
      })
      .pipe((prompt: any) => prompt.messages[0].content)
      .pipe((prompt: any) => assistant(prompt, this.openAi, thread))
      .pipe((response: any) => {
        return new StringOutputParser().invoke(response);
      });

    const responseStream = await composedChain.stream({ openAi: this.openAi });

    for await (const chunk of responseStream) {
      const cleaned = chunk.replace(/```html|```/g, '');
      this.answer.set(this.answer() + cleaned);
    }

    this.idle.set(false);

    textToSpeech(this.answer(), this.openAi)
      .invoke(this.answer())
      .then(async (audioBlob) => {
        const audioUrl = URL.createObjectURL(audioBlob.blob);
        const audio = new Audio(audioUrl);
        audio.play();
      });
  }
}
